import type { PurchaseMatchRateRow } from 'api/purchaseMatchRate.types'
import moment, { Moment } from 'moment'
import type { MonthRange } from 'slices/aiConversations/calloutTypes'

const MONTHS_SHOWN = 12
const MONTH_FORMAT = 'YYYY-MM-DD'

export type Column = {
  month: string
  soFar: boolean
}

export type TableRow = {
  locationId: number | null
  shares: Array<number | null>
}

type Counts = { purchases: number; matched: number }

// The callout's months up to the current one, at most the last MONTHS_SHOWN of them; without any, the last MONTHS_SHOWN months
export const buildColumns = (
  now: Moment,
  months: MonthRange | null
): Column[] => {
  const current = now.clone().startOf('month')
  const end = months
    ? moment.min(moment(months.end, MONTH_FORMAT), current)
    : current
  const requested = months
    ? end.diff(moment(months.start, MONTH_FORMAT), 'months') + 1
    : MONTHS_SHOWN
  // A range entirely in the future still shows the current month
  const count = Math.max(1, Math.min(requested, MONTHS_SHOWN))

  return Array.from({ length: count }, (_, index) => {
    const month = end.clone().subtract(count - 1 - index, 'months')
    return { month: month.format(MONTH_FORMAT), soFar: month.isSame(current) }
  })
}

const share = (counts?: Counts): number | null =>
  counts && counts.purchases > 0 ? counts.matched / counts.purchases : null

export const buildRows = (
  rows: PurchaseMatchRateRow[],
  columns: Column[],
  locationOrder: number[]
): TableRow[] => {
  const known = new Set(locationOrder)
  const byLocation = new Map<number, Map<string, Counts>>()
  const allLocations = new Map<string, Counts>()

  const add = (byMonth: Map<string, Counts>, row: PurchaseMatchRateRow) => {
    const counts = byMonth.get(row.month) ?? { purchases: 0, matched: 0 }
    byMonth.set(row.month, {
      purchases: counts.purchases + row.purchaseCount,
      matched: counts.matched + row.matchedPurchaseCount,
    })
  }

  rows.forEach(row => {
    add(allLocations, row)
    // Purchases with no location, or at one the locations list leaves out, count in All locations only
    if (row.locationId === null || !known.has(row.locationId)) return
    const byMonth = byLocation.get(row.locationId) ?? new Map()
    byLocation.set(row.locationId, byMonth)
    add(byMonth, row)
  })

  const toShares = (byMonth: Map<string, Counts>) =>
    columns.map(({ month }) => share(byMonth.get(month)))

  return [
    { locationId: null, shares: toShares(allLocations) },
    ...locationOrder.map(locationId => ({
      locationId,
      shares: toShares(byLocation.get(locationId) ?? new Map()),
    })),
  ]
}
